import { ICommand } from './ICommand';
import { useDiagramStore } from '../../stores/diagramStore';
import { getNodeMutationManager } from './mutations/NodeMutationManager';
import { Node } from '../models/scene';
import { Edge } from '../models/scene';
import { expandNodeIdsWithGroupMembers } from '../../lib/diagram/grouping';
import { ShapeMapAdapter } from '../../lib/diagram/ShapeMapAdapter';
import { ShapePersistenceGateway } from '../../lib/diagram/shapeGeometry';
import { getDataBinding } from '../../domains/data-binding/services/BoundShapeEditInterceptor';
import {
  clearDeletedNodeBindings,
  trackDeletedNodeBinding,
} from '../../domains/data-binding/utils/nodeBinding';
import { getSubtreeNodeIds } from '../../domains/orgchart/commands/IncrementalOrgLayoutCommand';
import { isOrgEdge } from '../../domains/orgchart/types/layout.types';
import { markNodeDirty, getGlobalApplyLayout } from '../../domains/orgchart/hooks/useAutoLayout';
import type {
  CommentPinningSnapshot,
  CommentRestorationCallbacks,
} from '../../domains/comments/models/Comment';
import { Tracker } from '../../domains/analytics';
import { TABLE } from '../../domains/analytics/constants/MetricEvents';
import type { IScenarioRecordable, CommandDeltaContribution } from '../data-profiles/scenarios/ScenarioRecordable';
import { createNodeId, createManagerId, createEdgeId } from '../data-profiles/scenarios/ScenarioDelta';
import {
  collectMediaKeysFromNodes,
  emitDiagramMediaReferenceDelta,
} from '../../domains/media/utils/commandMediaReferenceEvents';
import { isProjectionOwnedNode } from '../canvas/composeProjectedScene';

export class DeleteNodeCommand implements ICommand, IScenarioRecordable {
  id: string;
  name = 'Delete Nodes';
  description: string;
  timestamp = Date.now();
  readonly isScenarioRecordable = true as const;
  
  private nodeIds: string[];
  private deletedNodes: Node[] = [];
  private deletedEdges: Edge[] = [];
  private commentSnapshots: CommentPinningSnapshot[] = [];
  private commentCallbacks?: CommentRestorationCallbacks;
  
  constructor(
    nodeIds: string[],
    commentCallbacks?: CommentRestorationCallbacks
  ) {
    this.id = `delete-nodes-${Date.now()}`;
    this.nodeIds = nodeIds;
    this.description = nodeIds.length === 1 
      ? 'Delete node' 
      : `Delete ${nodeIds.length} nodes`;
    this.commentCallbacks = commentCallbacks;
  }

  async execute(): Promise<void> {
    // 1. Capture comment state BEFORE deletion (fresh snapshot each time)
    this.commentSnapshots = [];
    const store = useDiagramStore.getState();
    // Projection-owned nodes are canonical graph records. Their authored
    // geometry is movable, but deletion remains server-controlled.
    this.nodeIds = this.nodeIds.filter((id) => {
      const node = store.nodes.find((candidate) => candidate.id === id);
      return !node || !isProjectionOwnedNode(node);
    });
    if (this.nodeIds.length === 0) return;
    let expandedIds = expandNodeIdsWithGroupMembers(this.nodeIds, store.nodes);
    if (store.edges.some((e) => isOrgEdge(e))) {
      const withDescendants = new Set(expandedIds);
      for (const id of expandedIds) {
        getSubtreeNodeIds(id, store.edges).forEach((n) => withDescendants.add(n));
      }
      expandedIds = withDescendants;
    }
    expandedIds = new Set(Array.from(expandedIds).filter((id) => {
      const node = store.nodes.find((candidate) => candidate.id === id);
      return !node || !isProjectionOwnedNode(node);
    }));
    this.nodeIds = Array.from(expandedIds);
    if (this.nodeIds.length === 0) return;
    if (this.commentCallbacks && this.nodeIds.length > 0) {
      try {
        const comments = await this.commentCallbacks.getCommentsForShapes(this.nodeIds);
        this.commentSnapshots = comments
          .filter(c =>
            c.parentId === null &&
            c.shapeId !== null &&
            c.locOnShape !== null &&
            this.nodeIds.includes(c.shapeId)
          )
          .map(c => ({
            commentId: c.id,
            originalShapeId: c.shapeId!,
            originalLocOnShape: c.locOnShape!,
            originalLocOnDiagram: c.locOnDiagram,
          }));
      } catch (error) {
        // Log but don't fail - comment restoration is best-effort
        console.warn('[DeleteNodeCommand] Failed to capture comment state', error);
        this.commentSnapshots = [];
      }
    }

    // 2. Store deleted items for undo
    const mutationManager = getNodeMutationManager();
    
    this.deletedNodes = store.nodes.filter(node => 
      this.nodeIds.includes(node.id)
    );

    // Track table deletions
    const deletedTables = this.deletedNodes.filter((node) => (node as any).type === 'table');
    if (deletedTables.length > 0) {
      const diagramId =
        typeof window !== 'undefined'
          ? ((window as unknown as { __DIAGRAM_ID__?: string }).__DIAGRAM_ID__ ?? 'demo-diagram')
          : 'demo-diagram';
      for (const table of deletedTables) {
        Tracker.track(TABLE.DELETED, {
          tableId: table.id,
          diagramId,
        });
      }
    }
    
    // Also delete connected edges
    this.deletedEdges = store.edges.filter(edge => 
      this.nodeIds.includes(edge.from) || 
      this.nodeIds.includes(edge.to)
    );

    const affectedOrgManagers = new Set<string>();
    this.deletedEdges.forEach((edge) => {
      if (!isOrgEdge(edge)) return;
      // For org edges, `to` is the manager/parent side.
      if (!this.nodeIds.includes(edge.to)) {
        affectedOrgManagers.add(edge.to);
      }
    });
    
    // Delete edges and nodes in a SINGLE combined transaction to avoid multiple requests
    // This ensures all deletions happen in one Yjs transaction
    const ydoc = (mutationManager as any).ydoc;
    if (ydoc && (this.deletedEdges.length > 0 || this.nodeIds.length > 0)) {
      const shapes = new ShapePersistenceGateway(new ShapeMapAdapter(ydoc.getMap('shapes')));
      const connectorsMap = ydoc.getMap('connectors');
      const deleteSet = new Set(this.nodeIds);
      const bindingsToTrack = this.deletedNodes.flatMap((node) => {
        const binding = getDataBinding(node as unknown as Record<string, unknown>);
        if (!binding || binding.isNewRecord) return [];

        const hasRemainingSibling = store.nodes.some((candidate) => {
          if (deleteSet.has(candidate.id)) return false;
          const candidateBinding = getDataBinding(candidate as unknown as Record<string, unknown>);
          return (
            candidateBinding?.sourceId === binding.sourceId &&
            candidateBinding?.recordId === binding.recordId
          );
        });
        return hasRemainingSibling ? [] : [{ nodeId: node.id, binding }];
      });
      
      ydoc.transact(() => {
        bindingsToTrack.forEach(({ nodeId, binding }) => {
          trackDeletedNodeBinding(ydoc, nodeId, binding);
        });
        this.deletedEdges.forEach(edge => {
          connectorsMap.delete(edge.id);
        });
        
        this.nodeIds.forEach(nodeId => {
          shapes.delete(nodeId);
        });
      }, 'local');
      
      // DO NOT update store here - let Yjs → Store sync handle it
    } else {
      // No Yjs doc - use individual batch methods
      if (this.deletedEdges.length > 1) {
        (mutationManager as any).batchDeleteEdges(this.deletedEdges.map(e => e.id));
      } else if (this.deletedEdges.length === 1) {
        mutationManager.deleteEdge(this.deletedEdges[0].id);
      }
      
      if (this.nodeIds.length > 1) {
        (mutationManager as any).batchDeleteNodes(this.nodeIds);
      } else if (this.nodeIds.length === 1) {
        mutationManager.deleteNode(this.nodeIds[0]);
      }
    }

    const removedKeys = collectMediaKeysFromNodes(this.deletedNodes);
    emitDiagramMediaReferenceDelta({ removedKeys });

    if (affectedOrgManagers.size > 0) {
      // Ensure org auto-layout recalculates immediately after delete sync settles.
      // Without this, some delete paths can leave connector/node layout in an
      // intermediate state until the next user mutation.
      queueMicrotask(() => {
        const run = () => {
          affectedOrgManagers.forEach((managerId) => markNodeDirty(managerId));
          getGlobalApplyLayout()?.flush();
        };
        if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
          window.requestAnimationFrame(() => run());
        } else {
          run();
        }
      });
    }
    
    // Clear selection if deleted nodes were selected
    const currentSelection = store.selectedIds;
    const remainingSelection = new Set(
      Array.from(currentSelection).filter(id => !this.nodeIds.includes(id))
    );
    if (remainingSelection.size !== currentSelection.size) {
      store.setSelected(remainingSelection);
    }
  }
  
  async undo(): Promise<void> {
    const mutationManager = getNodeMutationManager();
    
    // 1. Restore nodes first - batch restore if multiple
    if (this.deletedNodes.length > 1) {
      (mutationManager as any).batchAddNodes(this.deletedNodes);
    } else if (this.deletedNodes.length === 1) {
      mutationManager.addNode(this.deletedNodes[0]);
    }
    
    // 2. Then restore edges - batch restore if multiple
    if (this.deletedEdges.length > 1) {
      (mutationManager as any).batchAddEdges(this.deletedEdges);
    } else if (this.deletedEdges.length === 1) {
      mutationManager.addEdge(this.deletedEdges[0]);
    }

    const ydoc = (mutationManager as any).ydoc as { transact: (fn: () => void, origin?: unknown) => void } | undefined;
    if (ydoc && this.deletedNodes.length > 0) {
      ydoc.transact(() => {
        clearDeletedNodeBindings(
          ydoc as unknown as import('yjs').Doc,
          this.deletedNodes.map((node) => node.id)
        );
      }, 'undo-delete-nodes-clear-binding-tracking');
    }

    // 3. Restore comment pinning AFTER shapes are restored
    if (this.commentCallbacks && this.commentSnapshots.length > 0) {
      // Mark comments as restored to prevent race condition with useEffect
      if (this.commentCallbacks.markCommentsAsRestored) {
        const restoredIds = this.commentSnapshots.map(s => s.commentId);
        this.commentCallbacks.markCommentsAsRestored(restoredIds);
      }

      // Restore each comment's pinning state
      const restorePromises = this.commentSnapshots.map(snapshot =>
        this.commentCallbacks!.updateComment(snapshot.commentId, {
          shapeId: snapshot.originalShapeId,
          locOnShape: snapshot.originalLocOnShape,
          locOnDiagram: snapshot.originalLocOnDiagram,
        }).catch(error => {
          // Log but don't fail - shapes are already restored
          console.warn(
            '[DeleteNodeCommand] Failed to restore comment pinning',
            { commentId: snapshot.commentId, error }
          );
        })
      );

      await Promise.allSettled(restorePromises);
    }

    const addedKeys = collectMediaKeysFromNodes(this.deletedNodes);
    emitDiagramMediaReferenceDelta({ addedKeys });
  }
  
  async redo(): Promise<void> {
    // Clear snapshots - will capture fresh ones on re-execute
    this.commentSnapshots = [];
    await this.execute();
  }

  toScenarioDelta(): CommandDeltaContribution {
    const store = useDiagramStore.getState();
    const expandedIds = expandNodeIdsWithGroupMembers(this.nodeIds, store.nodes);
    const nodesToDelete = store.nodes.filter(n => expandedIds.has(n.id));
    const edgesToDelete = store.edges.filter(e =>
      expandedIds.has(e.from) || expandedIds.has(e.to)
    );

    // Clear selection of deleted nodes (execute() is skipped in scenario mode)
    const currentSelection = store.selectedIds;
    const idsToRemove = new Set(expandedIds);
    const remainingSelection = new Set(
      Array.from(currentSelection).filter(id => !idsToRemove.has(id))
    );
    if (remainingSelection.size !== currentSelection.size) {
      store.setSelected(remainingSelection);
    }

    const removals = nodesToDelete.map(node => {
      const attrs = (node.attrs ?? {}) as Record<string, unknown>;
      return {
        nodeId: createNodeId(node.id),
        originalData: {
          name: (attrs.name as string) ?? node.name ?? node.id,
          title: (attrs.title as string) ?? (attrs.role as string),
          managerId: attrs.managerId
            ? createManagerId(attrs.managerId as string)
            : null,
          type: node.type,
        },
      };
    });

    const edgeRemovals = edgesToDelete.map(edge => ({
      edgeId: createEdgeId(edge.id),
      // Preserve the generic canvas cascade while identifying the owning node
      // so domain commit bridges do not mistake it for an independent edit.
      cascadeFromNodeId: createNodeId(
        expandedIds.has(edge.from) ? edge.from : edge.to,
      ),
    }));

    return { removals, edgeRemovals };
  }

  getUndoDelta(): CommandDeltaContribution {
    const store = useDiagramStore.getState();
    const expandedIds = expandNodeIdsWithGroupMembers(this.nodeIds, store.nodes);
    const nodesToRestore = store.nodes.filter(n => expandedIds.has(n.id));
    const edgesToRestore = store.edges.filter(e =>
      expandedIds.has(e.from) || expandedIds.has(e.to)
    );

    const additions = nodesToRestore.map(node => {
      const attrs = (node.attrs ?? {}) as Record<string, unknown>;
      return {
        id: createNodeId(node.id),
        type: node.type,
        name: (attrs.name as string) ?? node.name ?? node.id,
        title: (attrs.title as string) ?? (attrs.role as string),
        managerId: attrs.managerId
          ? createManagerId(attrs.managerId as string)
          : null,
        position: { x: node.x ?? 0, y: node.y ?? 0 },
      };
    });

    const edgeAdditions = edgesToRestore.map(edge => ({
      id: createEdgeId(edge.id),
      type: edge.type as string,
      from: createNodeId(edge.from),
      to: createNodeId(edge.to),
    }));

    return { additions, edgeAdditions };
  }
}
